import { useQuery } from '@apollo/client';
import React from 'react';
import * as styles from './SeriesHoverCard.module.scss';
import SeriesIcon from './SeriesIcon';
import SeriesRow from './SeriesRow';
import useSeriesPolicyCheck, {
  SeriesPolicyAction,
} from './useSeriesPolicyCheck';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import Button from '../components/Button';
import { CardSeparator } from '../components/Card';
import CardHeader from '../components/CardHeader';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { UIActions } from '../ui/uiSlice';

// Use dynamic import to avoid circular dependency
const EditSeriesModal = import('./modal/EditSeriesModal');

const SERIES_HOVER_CARD_PUBLIC_SERIES_FRAGMENT = gql(`
  fragment SeriesHoverCard_series on SeriesFields {
    id
    name
    posts {
      id
      ...SeriesRow_post
    }
  }
`);

const SERIES_QUERY = gql(/** @complexity 14 */ `
  query SeriesHoverCard($id: ID!) {
    post(id: $id) {
      id
      series {
        ...SeriesHoverCard_series
        ...useSeriesPolicyCheck_series
      }
    }
  }
`);

interface SeriesHoverCardProps {
  postId: string;
  showEditButton?: boolean;
  onClickEditButton?: () => void;
  series?: DocumentType<typeof SERIES_HOVER_CARD_PUBLIC_SERIES_FRAGMENT>;
}

const SeriesHoverCard = (props: SeriesHoverCardProps) => {
  const { postId } = props;
  const currentUser = useAppSelector((state) => state.session.user);
  const dispatch = useAppDispatch();
  const seriesPolicyCheck = useSeriesPolicyCheck();

  const { data } = useQuery(SERIES_QUERY, {
    variables: { id: postId },
    skip: !!props.series,
  });

  const fetchedSeries = data?.post.series ?? null;

  const series = props.series ?? fetchedSeries;

  if (!series) {
    return null;
  }

  return (
    <div className={styles.container}>
      <CardHeader
        icon={<SeriesIcon />}
        aboveTitle={<div className={styles.meta}>Series</div>}
        title={series.name}
        rightAction={
          fetchedSeries &&
          props.showEditButton &&
          currentUser?.id &&
          seriesPolicyCheck(
            SeriesPolicyAction.EditSeries,
            fetchedSeries,
            currentUser,
          ) && (
            <Button
              variant="secondary"
              onClick={async () => {
                const { default: Modal } = await EditSeriesModal;
                dispatch(UIActions.openModal(Modal, { seriesId: series.id }));
                props.onClickEditButton?.();
              }}
            >
              Edit
            </Button>
          )
        }
      />
      <CardSeparator />
      {series.posts.map((post, index) => (
        <SeriesRow
          key={post.id}
          active={post.id === postId}
          index={index}
          post={post}
        />
      ))}
    </div>
  );
};

export default SeriesHoverCard;
